import AsyncAutocomplete from "@/modules/common/components/ui-kit/AsyncAutocomplete";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { Box, Button, FormHelperText, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { addInterestSchema } from "../../../utils/interests";
import z from "zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import useAddInterest from "../../../hooks/interests/add-interest.hook";
import { showToast } from "@/modules/common/utils";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import { Plus } from "lucide-react";
import { usePropertiesForInterestsInfiniteQuery } from "../../../hooks/interests/properties-for-interests.hook";

type AddInterestFormFields = z.infer<ReturnType<typeof addInterestSchema>>;

function AddInterest() {
  const { lead } = useContext(LeadPageContext);

  const [open, setOpen] = useState(false);

  const [inputValue, setInputValue] = useState<string | undefined>();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    usePropertiesForInterestsInfiniteQuery({
      leadTypeUid: lead?.data.lead_type.id as string,
      journeyUid: lead?.data.uid as string,
      search: inputValue,
      enabled: open,
    });

  const mutation = useAddInterest();

  const { handleSubmit, reset, control } = useForm<AddInterestFormFields>({
    resolver: zodResolver(addInterestSchema()),
  });

  const onSubmit: SubmitHandler<AddInterestFormFields> = async (data) => {
    submitAddInterest(data);
  };

  const options =
    data?.pages.flatMap((page) =>
      page.data.results
        ? page.data.results.map((p) => ({
            value: p,
            key: p.uid,
          }))
        : []
    ) ?? [];

  const submitAddInterest = (data: AddInterestFormFields) => {
    mutation.mutate(
      {
        data: {
          leadTypeUid: lead?.data.lead_type.id as string,
          journeyUid: lead?.data.uid as string,
          propertyUids: data.propertyUids,
        },
      },
      {
        onSuccess: () => {
          handleCloseModal();
          showToast({
            type: ToastType.SUCCESS,
            message: `Interest added successfully.`,
            toastId: TOAST_IDS.ADD_INTEREST_SUCCESS,
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_INTEREST_ERROR,
          });
        },
      }
    );
  };

  const handleOpenModal = () => {
    reset();
    setOpen(true);
  };

  const handleCloseModal = () => {
    reset();
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpenModal}
        sx={(theme) => ({
          display: "flex",
          gap: theme.nawy.spacing.xs,
          alignItems: "center",
          backgroundColor: theme.nawy.colors.semantic.layer.accent,
          textTransform: "none",
          borderRadius: theme.nawy.radius.lg,
        })}
      >
        <Plus size={16} />
        Add Interest
      </Button>
      <EditFieldModal
        open={open}
        setOpen={setOpen}
        header="Add Interest"
        width={480}
        handleConfirm={handleSubmit(onSubmit)}
        handleCloseModal={handleCloseModal}
        isLoading={mutation.isPending}
      >
        <Controller
          control={control}
          name="propertyUids"
          render={({ field, fieldState }) => (
            <>
              <AsyncAutocomplete
                infinite
                options={options}
                onChange={(value) => {
                  field.onChange(value?.map((v) => v.key));
                }}
                onInputChange={setInputValue}
                isLoading={isLoading}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage ?? false}
                multiple={true}
                maxSelected={5}
                fetchNextPage={fetchNextPage}
                // Pass the functions down
                label="Select Properties"
                placeholder="Search by property ID or location..."
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.key}
                    sx={(t) => ({
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "stretch",
                      textAlign: "start",
                      width: "100%",
                      gap: t.nawy.spacing.xxs,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: t.nawy.colors.semantic.state.onColor08,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      },
                    })}
                  >
                    <Typography
                      sx={{
                        textAlign: "start",
                        width: "100%",
                      }}
                    >
                      {option.value.unitId}
                    </Typography>
                    <Typography
                      sx={(t) => ({
                        ...t.nawy.typography.english.variants.body.sm.regular,
                        textAlign: "start",
                        width: "100%",
                      })}
                    >
                      {option.value.propertyType?.name} in{" "}
                      {option.value.compound?.name}
                      {" • "}
                      {option.value.unitArea} m² {" • "}
                      {option.value.numberOfBedrooms} bedrooms {" • "}
                      {option.value.propertyPrice?.askingPrice?.toLocaleString() ||
                        option.value.propertyRental?.rentalPrice?.toLocaleString()}{" "}
                      EGP
                    </Typography>
                  </Box>
                )}
                getOptionLabel={(option) =>
                  `${option.value.propertyType?.name} in ${option.value.compound?.name}`
                }
              />
              {fieldState.error && (
                <FormHelperText error>
                  {fieldState.error.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </EditFieldModal>
    </>
  );
}

export default AddInterest;
